import autobind from 'core-decorators/lib/autobind';
import classnames from 'classnames';
import { connect } from 'react-redux';

import { WixConnect } from 'shared/utils/wix-connect';
import ResizedImage from 'shared/components/resized-image/resized-image';
import { notForPreview } from 'widget/utils/not-for-preview';
import { isExternalVideo } from 'shared/ui-selectors/video/video-origin';

import VIDEO_PLAYBACK_STATUSES from 'widget/constants/video-playback-status';

import { mainItemTypeValues, posterPositionValues } from 'default-settings';
import videoSources from 'shared/constants/video-sources';

import { setPlaybackStatus } from 'widget/redux/client/actions/change-playback-status';

import Video from './video/video';
import Controls from './controls/controls';
import Overlay from './overlay/overlay';
import OverlayActions from './overlay-actions/overlay-actions';
import InfoOverlay from './info-overlay/info-overlay';
import { getOverlayActions, OVERLAY_ACTION_TYPES } from './overlay-actions/overlay-action-utils';

import FullscreenTracker from './fullscreen/fullscreen-changed-tracker';
import { exitFullscreen, requestFullscreen, isInFullscreen } from './fullscreen/utils';


import styles from './player.styl';

import contextTypes from './context-types';


const STORYBOARD_TYPE = 'storyboard';
const DELAY_BEFORE_HIDE_CONTROL = 3000;

@connect(null,
  {
    setPlaybackStatus,
  }
)
@WixConnect(styles)
export default class Player extends React.Component {

  static propTypes = {
    children: React.PropTypes.any,
    videoItem: React.PropTypes.object.isRequired,
    videoUrls: React.PropTypes.object,
    channelData: React.PropTypes.object,
    appSettings: React.PropTypes.object,
    className: React.PropTypes.string,
    width: React.PropTypes.number,
    height: React.PropTypes.number,
    onError: React.PropTypes.func,
    onPlayStart: React.PropTypes.func,
    onPause: React.PropTypes.func,
    onResume: React.PropTypes.func,
    showChannelCover: React.PropTypes.bool,
    canShowChannelCover: React.PropTypes.bool,
    posterPosition: React.PropTypes.string,
    simple: React.PropTypes.bool,
    mobileMode: React.PropTypes.bool,
    onEnded: React.PropTypes.func,
    onPurchaseClick: React.PropTypes.func,
    onSubscribeClick: React.PropTypes.func,
    onFullScreenClicked: React.PropTypes.func,
    onFullScreenChanged: React.PropTypes.func,
    openPreviewPrompt: React.PropTypes.func,
    onShowActions: React.PropTypes.func,
    onActionClicked: React.PropTypes.func,
    isFetchDisabled: React.PropTypes.bool,

    setPlaybackStatus: React.PropTypes.func,

    volume: React.PropTypes.number,
    currentTime: React.PropTypes.number,
    paused: React.PropTypes.bool,
    playAtFullScreen: React.PropTypes.bool,
    muted: React.PropTypes.bool,
    loop: React.PropTypes.bool,
    showInitialOverlay: React.PropTypes.bool,
    showPoster: React.PropTypes.bool,
    showControls: React.PropTypes.bool,
  };

  static defaultProps = {
    onError: _.noop,
    onPlayStart: _.noop,
    onPause: _.noop,
    onResume: _.noop,
    openPreviewPrompt: _.noop,
    onShowActions: _.noop,
    onActionClicked: _.noop,
    onFullScreenClicked: _.noop,
    onFullScreenChanged: _.noop,
    onEnded: _.noop,
    paused: true,
    playAtFullScreen: false,
    muted: false,
    volume: 1,
    canShowChannelCover: false,
    posterPosition: posterPositionValues.CENTER,
    simple: false,
    mobileMode: false,
    width: 640,
    height: 360,
    loop: false,
    showInitialOverlay: false,
    showPoster: true,
    showControls: true,
  };

  static childContextTypes = contextTypes;

  constructor(props) {
    super(props);

    const { videoUrls, paused, muted, volume, loop, showControls } = this.props;

    this.state = {
      paused,
      muted,
      volume,
      loop,
      showControls,
      currentTime: 0,
      userTime: 0,

      infoOverlay: null,

      fullScreen: false,
      error: false,
      loading: false,
      beenPlayed: false,
      ended: false,
      fadeControls: true,

      ...this.parseVideoUrls(videoUrls),
    };
  }

  getChildContext() {
    const props = _.pick(this.props, [
      'simple',
      'mobileMode',
      'videoItem',
      'channelData',
      'height',
      'width',
      'canShowChannelCover',
      'showChannelCover',
      'openPreviewPrompt',
      'muted',
      'loop',
    ]);

    const context = _.merge({
      updateStateFromVideo: this.updateStateFromVideo,
      togglePause: this.togglePause,
      toggleMute: this.toggleMute,
      toggleError: this.toggleError,
      exitFullScreen: this.exitFullScreen,
      toggleFullScreenByUser: this.toggleFullScreenByUser,
      toggleEnded: this.toggleEnded,
      toggleBeenPlayed: this.toggleBeenPlayed,
      toggleInfoOverlay: this.toggleInfoOverlay,
      setVolume: this.setVolume,
      seek: this.seek,
      resetSeek: this.resetSeek,
      afterShareClicked: this.afterShareClicked,
    }, props, this.state);

    return context;
  }

  componentDidMount() {
    const { videoItem, isFetchDisabled, paused, playAtFullScreen } = this.props;
    const { videoUrls } = this.state;
    if (!videoUrls && !isFetchDisabled) {
      this.loadVideoUrls(videoItem);
    }
    if (this.shouldShowOverlay()) {
      this.props.onShowActions(this.actionsList);
    }

    if (playAtFullScreen && !paused) {
      this.requestFullScreen(videoItem);
    }

    this.wrapper.addEventListener('mousemove', this.handleMouseMove);
  }

  componentWillReceiveProps(nextProps) {
    const {
      videoItem,
      videoUrls,
      paused: nextPaused,
      isFetchDisabled,
      playAtFullScreen,
      currentTime,
      muted: nextMuted,
      loop: nextLoop,
      showControls: nextShowControls,
    } = nextProps;

    if (this.props.videoItem.id === videoItem.id) {
      if (isFetchDisabled) {
        this.setVideoUrls(videoUrls);
      }
    } else {
      this.toggleInfoOverlay();
      this.toggleEnded(false);
      this.toggleError(false);
      this.toggleBeenPlayed(false);
      this.seek(0);
      if (isFetchDisabled) {
        this.setVideoUrls(videoUrls);
      } else {
        this.loadVideoUrls(videoItem);
      }
    }

    if (this.state.paused !== nextPaused) {
      this.togglePause(nextPaused);
    }

    if (this.state.muted !== nextMuted) {
      this.toggleMute(nextMuted);
    }

    if (this.state.loop !== nextLoop) {
      this.toggleLoop(nextLoop);
    }

    if (this.state.showControls !== nextShowControls) {
      this.toggleControls(nextShowControls);
    }

    // requestFullScreen is a user generated command
    // only true can be received and handled in 'componentWillReceiveProps'
    if (playAtFullScreen && this.hasStartedPlaying(nextPaused, this.state.paused)) {
      this.requestFullScreen(videoItem);
    }

    this.seek(currentTime);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.shouldShowOverlay() && !this.shouldShowOverlay(prevProps, prevState)) {
      this.props.onShowActions(this.actionsList);
    }
  }

  componentWillUnmount() {
    this.wrapper.removeEventListener('mousemove', this.handleMouseMove);
  }

  hasStartedPlaying(nextPaused, currentPaused) {
    return !nextPaused && currentPaused;
  }

  @autobind
  handleMouseMove() {
    const { beenPlayed, paused, ended, fadeControls } = this.state;

    if (beenPlayed && !ended) {
      if (!fadeControls) {
        this.fadeInControls();
      }

      if (!paused) {
        this.updateFadeControlsTimer();
      }
    }
  }

  updateFadeControlsTimer() {
    this.removeFadeControlsTimer();
    this.controlsTimer = setTimeout(this.fadeOutControls, DELAY_BEFORE_HIDE_CONTROL);
  }

  removeFadeControlsTimer() {
    clearTimeout(this.controlsTimer);
    this.fadeInControls();
  }

  fadeInControls() {
    if (!this.state.fadeControls) {
      this.setState({
        fadeControls: true,
      });
    }
  }

  @autobind
  fadeOutControls() {
    if (this.state.fadeControls) {
      this.setState({
        fadeControls: false,
      });
    }
  }

  @autobind
  afterShareClicked(networkName) {
    this.props.onActionClicked(OVERLAY_ACTION_TYPES.SHARE, { networkName });
  }

  shouldShowOverlay(props, state) {
    const { simple, mobileMode } = props || this.props;
    return !this.shouldShowInitialOverlay(props, state) && !simple && !mobileMode;
  }

  @autobind
  playVideo() {
    const { videoItem } = this.props;
    const { durationSec } = videoItem;

    this.togglePause(false);
    this.toggleEnded(false);
    this.toggleError(false);
    this.seek(0);
    this.setState({ duration: durationSec });
  }

  @autobind
  updateStateFromVideo(state) {
    this.setState(state);
  }

  @autobind
  seek(time) {
    this.setState({
      userTime: time,
    });
  }

  @autobind
  resetSeek() {
    this.seek(null);
  }

  @autobind
  toggleStateBooleanField(name, value) {
    if (!_.isBoolean(value)) {
      value = !this.state[name];
    }

    this.setState({
      [name]: value,
    });

    return value;
  }

  get videoRef() {
    return this._videoRef;
  }

  get isInFullScreen() {
    return isInFullscreen(this.videoRef);
  }

  @autobind
  handleFullScreenChange() {
    const isInFullScreen = this.isInFullScreen;

    if (!isInFullScreen) {
      this.toggleInfoOverlay();
    }
    this.props.onFullScreenChanged(isInFullScreen);

    this.toggleStateBooleanField('fullScreen', isInFullScreen);
  }

  @autobind
  exitFullScreen() {
    exitFullscreen(this.videoRef);
  }

  @autobind
  @notForPreview
  requestFullScreen(videoItem) {
    if (isExternalVideo(videoItem)) {
      return;
    }
    requestFullscreen(this.wrapper, this.videoRef);
  }

  @autobind
  toggleFullScreenByUser() {
    const { onFullScreenClicked, videoItem } = this.props;
    const isInFullScreen = this.isInFullScreen;

    onFullScreenClicked(isInFullScreen);

    if (isInFullScreen) {
      this.exitFullScreen();
    } else {
      this.requestFullScreen(videoItem);
    }
  }

  @autobind
  toggleLoading(loading) {
    this.toggleStateBooleanField('loading', loading);
  }

  @autobind
  toggleError(error) {
    this.toggleStateBooleanField('error', error);
  }

  @autobind
  toggleMute(muted) {
    this.toggleStateBooleanField('muted', muted);
  }

  @autobind
  toggleLoop(loop) {
    this.toggleStateBooleanField('loop', loop);
  }

  @autobind
  toggleControls(showControls) {
    this.toggleStateBooleanField('showControls', showControls);
  }

  @autobind
  togglePause(nextPaused) {
    const { setPlaybackStatus, onPause, onResume } = this.props;
    const { paused, beenPlayed } = this.state;
    if (paused === nextPaused) {
      return;
    }

    this.toggleStateBooleanField('paused', nextPaused);

    if (nextPaused) {
      setPlaybackStatus(VIDEO_PLAYBACK_STATUSES.PAUSED);
      onPause();
    } else {
      if (beenPlayed) {
        onResume();
      }
      setPlaybackStatus(VIDEO_PLAYBACK_STATUSES.PLAYING);
    }

    if (nextPaused === true) {
      this.removeFadeControlsTimer();
    } else {
      this.updateFadeControlsTimer();
    }
  }

  @autobind
  toggleBeenPlayed(beenPlayed) {
    const { beenPlayed: prevBeenPlayed } = this.state;

    if (beenPlayed && beenPlayed !== prevBeenPlayed) {
      this.props.onPlayStart();
    }

    this.toggleStateBooleanField('beenPlayed', beenPlayed);
  }

  @autobind
  toggleEnded(ended) {
    const { setPlaybackStatus, onEnded } = this.props;
    if (ended) {
      this.exitFullScreen();
      setPlaybackStatus(VIDEO_PLAYBACK_STATUSES.ENDED);
      onEnded();
      this.toggleBeenPlayed(false);
    }

    this.toggleStateBooleanField('ended', ended);
  }

  @autobind
  toggleInfoOverlay(infoOverlay = null, infoOverlayProps = null) {
    this.setState({
      infoOverlay,
      infoOverlayProps,
    });
  }

  @autobind
  setVolume(volume = 1) {
    this.setState({
      volume,
    });
  }

  parseVideoUrls(videoUrls) {
    if (videoUrls) {
      return {
        videoUrls: _.omit(videoUrls, STORYBOARD_TYPE),
        storyboardUrl: videoUrls[STORYBOARD_TYPE],
      };
    }

    return {
      videoUrls: null,
      storyboardUrl: null,
    };
  }

  @autobind
  setVideoUrls(videoUrls) {
    this.setState(this.parseVideoUrls(videoUrls));
  }

  get shouldDisplayExternalPlayer() {
    return _.includes([
      videoSources.VIMEO,
      videoSources.FACEBOOK,
    ], this.props.videoItem.videoSource);
  }

  @autobind
  loadVideoUrls(videoItem) {
    this.setVideoUrls(null);

    const { appSettings: { instance } } = this.props;
    const { getVideoUrls } = videoItem;

    if (this.shouldDisplayExternalPlayer || !getVideoUrls) {
      return;
    }

    getVideoUrls(instance)
      .then(videoUrls => {
        this.setVideoUrls(videoUrls);
      })
      .catch(() => {
        this.toggleLoading(false);
        this.toggleError(true);
      });
  }

  get showChannelCover() {
    const { appSettings, canShowChannelCover } = this.props;
    return canShowChannelCover && appSettings.numbers.mainItemType === mainItemTypeValues.CHANNEL_COVER;
  }

  get posterUrl() {
    const { videoItem, channelData } = this.props;

    const { customCoverUrl } = videoItem;
    let { coverUrl } = videoItem;

    coverUrl = customCoverUrl || coverUrl;

    if (this.showChannelCover) {
      coverUrl = channelData.customCoverUrl || channelData.coverUrl || coverUrl;
    }

    return coverUrl;
  }

  // fix for Vidi
  get poster() {
    const {
      simple,
      showPoster,
      videoItem,
      width,
      height,
    } = this.props;

    if (!showPoster) {
      return null;
    }

    if (simple && isExternalVideo(videoItem)) {
      return null;
    }

    const posterUrl = this.posterUrl;

    if (!posterUrl) {
      return null;
    }

    if (!this.shouldShowInitialOverlay()) {
      return null;
    }

    return (
      <ResizedImage styleName="cover-image"
                    src={posterUrl}
                    width={width}
                    height={height} />
    );
  }

  get video() {
    const { videoUrls, beenPlayed, paused } = this.state;
    const { width, height, videoItem, onError, simple, mobileMode } = this.props;

    // do not render player before user start playing
    if (!beenPlayed && paused && !simple && !mobileMode && !isExternalVideo(videoItem)) {
      return null;
    }

    return (
      <Video styleName="video"
             controls
             onError={onError}
             width={width}
             height={height}
             videoItem={videoItem}
             refReceived={this.setVideoRef}
             videoUrls={videoUrls} />
    );
  }

  get styles() {
    if (this.isInFullScreen) {
      return {
        width: 'auto',
        height: 'auto',
      };
    }

    const { width, height } = this.props;
    return {
      width,
      height,
    };
  }

  get infoOverlay() {
    const { simple, mobileMode, videoItem } = this.props;
    if (isExternalVideo(videoItem)) {
      return null;
    }

    const { infoOverlay, infoOverlayProps } = this.state;

    if (simple || mobileMode || infoOverlay === null) {
      return null;
    }

    return (
      <InfoOverlay content={infoOverlay}
                   {...infoOverlayProps} />
    );
  }

  get initialOverlay() {
    const { children } = this.props;

    if (!children) {
      return null;
    }

    const { channelData } = this.props;
    const { error, beenPlayed, ended } = this.state;
    const originProps = _.pick(this.props, 'showChannelCover', 'appSettings', 'videoItem');

    if (error) {
      return null;
    }

    if (!this.shouldShowInitialOverlay()) {
      return null;
    }

    return React.cloneElement(children, _.assign({
      channelData,
      showChannelCover: this.showChannelCover,
      beenPlayed,
      ended,
      playVideoItem: this.playVideo,
      togglePause: this.togglePause,
    }, originProps));
  }

  shouldShowInitialOverlay(props = this.props, state = this.state) {
    const { videoUrls, mobileMode, beenPlayed, ended, paused } = state;

    if (!videoUrls && mobileMode) {
      return false;
    }

    // turn on overlay manually
    if (props.showInitialOverlay) {
      return true;
    }

    if (props.playAtFullScreen || this.isInFullScreen) {
      return false;
    }

    if (beenPlayed && !ended) {
      return false;
    }

    if (!paused) {
      return false;
    }

    return true;
  }

  get actionsList() {
    const { appSettings, channelData, videoItem } = this.props;
    return getOverlayActions(appSettings, channelData, videoItem);
  }

  get overlay() {
    const { appSettings, simple, onPurchaseClick, onSubscribeClick, mobileMode, videoItem } = this.props;

    if (isExternalVideo(videoItem)) {
      return null;
    }

    if (simple || mobileMode) {
      return null;
    }

    return (
      <Overlay styleName="overlay"
               actions={
                 <OverlayActions actionsList={this.actionsList}
                                 appSettings={appSettings}
                                 onActionClicked={this.props.onActionClicked}
                                 onSubscribeClick={onSubscribeClick}
                                 onPurchaseClick={onPurchaseClick} />
               } />
    );
  }

  get controls() {
    if (!this.state.showControls || isExternalVideo(this.props.videoItem)) {
      return null;
    }

    return (
      <Controls styleName="controls">
        <section styleName="controls-background"
                 onClick={this.togglePause} />
      </Controls>
    );
  }

  @autobind
  setVideoRef(videoRef) {
    // TODO: move to state instead of 'this'
    this._videoRef = videoRef;
  }

  render() {
    const { simple, playAtFullScreen, mobileMode, className } = this.props;
    const { paused, ended, beenPlayed, infoOverlay, fadeControls } = this.state;

    const styleNames = classnames('player', {
      simple,
      'not-been-played': !beenPlayed,
      'been-played': fadeControls && beenPlayed,
      'info-overlay-shown': fadeControls && (Boolean(infoOverlay) || (playAtFullScreen && paused && !mobileMode && !this.isInFullScreen)),
      ended,
      paused,
    });

    return (
      <section styleName={styleNames}
               className={className}
               style={this.styles}
               ref={node => (this.wrapper = node)}
               data-hook="player"
               data-player-fullscreen={this.isInFullScreen}
               data-player-state={paused ? 'paused' : 'playing'}>

        <FullscreenTracker videoRef={this.videoRef}
                           onFullscreenChanged={this.handleFullScreenChange} />

        {this.initialOverlay}

        {this.video}
        {this.poster}

        {this.overlay}
        {this.infoOverlay}

        {this.controls}
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/player-widget/components/player/player.js